<template>
    <div 
        ref="draggableElement"
        class="draggable-wrapper"
        :class="{ 'dragging': isDragging, 'disabled': !enabled }"
        :style="wrapperStyle"
        @mousedown="startDrag"
        @touchstart="startDrag"
    >
        <slot></slot>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, onUnmounted, computed, watch } from 'vue'
import appStore from '../../../store/app.store'

const props = defineProps({
    // Position props - can be controlled externally or managed internally
    x: {
        type: Number,
        default: null
    },
    y: {
        type: Number,
        default: null
    },
    // Initial position if not provided via x/y props
    initialX: {
        type: Number,
        default: 0
    },
    initialY: {
        type: Number,
        default: 0
    },
    // Enable/disable dragging
    enabled: {
        type: Boolean,
        default: true
    },
    // Only allow dragging from handle elements (class: 'handle' or 'cursor-move')
    handleOnly: {
        type: Boolean,
        default: true
    },
    // Position type: 'fixed' or 'absolute'
    positionType: {
        type: String,
        default: 'fixed',
        validator: (value) => ['fixed', 'absolute', 'relative'].includes(value)
    },
    // Z-index when dragging
    dragZIndex: {
        type: Number,
        default: 1000
    },
    // Constrain dragging to viewport
    constrainToViewport: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['update:position', 'update:x', 'update:y', 'startDrag', 'drag', 'endDrag', 'mouseout'])

const draggableElement = ref(null)
const isDragging = ref(false)
const dragStart = ref({ x: 0, y: 0 })
const elementStart = ref({ x: 0, y: 0 })
const parentOffset = ref({ x: 0, y: 0 })

// Internal position state (used when x/y props are not provided)
const internalX = ref(props.x !== null ? props.x : props.initialX)
const internalY = ref(props.y !== null ? props.y : props.initialY)

// Computed position - use props if provided, otherwise use internal state
const currentX = computed(() => {
    if (props.x !== null) return props.x
    return internalX.value
})

const currentY = computed(() => {
    if (props.y !== null) return props.y
    return internalY.value
})

// Watch for external position changes
watch(() => props.x, (newX) => {
    if (newX !== null) {
        internalX.value = newX
    }
})

watch(() => props.y, (newY) => {
    if (newY !== null) {
        internalY.value = newY
    }
})

// Wrapper style with position
const wrapperStyle = computed(() => {
    const baseStyle = {
        position: props.positionType,
        left: `${currentX.value}px`,
        top: `${currentY.value}px`,
        cursor: props.enabled && !props.handleOnly ? 'move' : 'default',
        userSelect: 'none'
    }
    
    if (isDragging.value) {
        baseStyle.zIndex = props.dragZIndex
        baseStyle.cursor = 'grabbing'
    }
    
    return baseStyle
})

// Calculate parent offset to avoid mouse-pointer offset issues
const calculateParentOffset = () => {
    if (draggableElement.value) {
        const parent = draggableElement.value.offsetParent
        if (parent) {
            const parentRect = parent.getBoundingClientRect()
            parentOffset.value = {
                x: parentRect.left,
                y: parentRect.top
            }
        }
    }
}


const startDrag = (event) => {
    if (!props.enabled) return
    
    emit('startDrag', event)
    
    // Check if dragging should only start from handle
    if (props.handleOnly) {
        const target = event.target
        const isHandle = target.classList.contains('handle') || 
                        target.closest('.handle') || 
                        target.classList.contains('cursor-move') ||
                        target.closest('.cursor-move')
        
        if (!isHandle) {
            return
        }
    }

    appStore.state.disableEvents = true
    event.preventDefault()
    event.stopPropagation()
    isDragging.value = true
    
    calculateParentOffset()
    
    // Get initial mouse/touch position
    const clientX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX
    const clientY = event.type.includes('touch') ? event.touches[0].clientY : event.clientY
    
    dragStart.value = {
        x: clientX,
        y: clientY
    }
    
    // Store initial element position
    elementStart.value = {
        x: currentX.value,
        y: currentY.value
    }
    
    // Add global event listeners
    document.addEventListener('mousemove', drag)
    document.addEventListener('touchmove', drag, { passive: false })
    document.addEventListener('mouseup', endDrag)
    document.addEventListener('touchend', endDrag)
    document.addEventListener('mouseout', handleMouseOut)

    // Calculate constraints once at start if enabled
    if (props.constrainToViewport && draggableElement.value) {
        const rect = draggableElement.value.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        
        dragConstraints.value = {
            minX: 0,
            maxX: viewportWidth - rect.width,
            minY: 0,
            maxY: viewportHeight - rect.height
        }
    }
}

const handleMouseOut = (event) => {
    emit('mouseout', event)
}

const dragConstraints = ref(null)

const drag = (event) => {
    if (!isDragging.value) return
    emit('drag', event)
    event.preventDefault()
    
    const clientX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX
    const clientY = event.type.includes('touch') ? event.touches[0].clientY : event.clientY
    
    // Calculate the difference from the initial drag position
    const deltaX = clientX - dragStart.value.x
    const deltaY = clientY - dragStart.value.y
    
    // Calculate new position
    let newX = elementStart.value.x + deltaX
    let newY = elementStart.value.y + deltaY
    
    // Constrain to viewport if enabled and constraints calculated
    if (props.constrainToViewport && dragConstraints.value) {
        newX = Math.max(dragConstraints.value.minX, Math.min(dragConstraints.value.maxX, newX))
        newY = Math.max(dragConstraints.value.minY, Math.min(dragConstraints.value.maxY, newY))
    }
    
    // Update internal position
    internalX.value = newX
    internalY.value = newY
    
    // Emit position updates
    emit('update:position', { x: newX, y: newY })
    emit('update:x', newX)
    emit('update:y', newY)
}

const endDrag = (event) => {
    appStore.state.disableEvents = false
    if (!isDragging.value) return
    
    isDragging.value = false
    emit('endDrag', event)
    
    // Remove global event listeners
    document.removeEventListener('mousemove', drag)
    document.removeEventListener('touchmove', drag)
    document.removeEventListener('mouseup', endDrag)
    document.removeEventListener('touchend', endDrag)
    document.removeEventListener('mouseout', handleMouseOut)
}

// Cleanup on unmount
onUnmounted(() => {
    document.removeEventListener('mousemove', drag)
    document.removeEventListener('touchmove', drag)
    document.removeEventListener('mouseup', endDrag)
    document.removeEventListener('touchend', endDrag)
    document.removeEventListener('mouseout', handleMouseOut)
})
</script>

<style scoped>
.draggable-wrapper {
    user-select: none;
}

.draggable-wrapper.dragging {
    cursor: grabbing !important;
}

.draggable-wrapper.dragging * {
    pointer-events: none;
}

.draggable-wrapper.disabled {
    cursor: default !important;
}

/* Handle styles */
.handle {
    cursor: move;
    user-select: none;
}

.handle.dragging {
    cursor: grabbing;
    z-index: 1000;
}

.handle.dragging * {
    pointer-events: none;
}

/* Ensure nested handles work */
.handle .handle {
    cursor: move;
}

.handle.dragging .handle {
    cursor: grabbing;
}

.cursor-move {
    cursor: move;
}
</style>


